import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { uid } from 'react-uid';
import type { GetDataError } from 'restful-react';
import type { ResponseApiErrors, Token } from '../../app/ApiGen';
import type { InputComponentProps } from '../../design-system';
import { AutocompleteOption } from '../AutocompleteOption';
import { type AutocompleteContents, AutocompleteWrapper, renderAutocompleteInput } from './SimpleAutocomplete';

export interface TokenInputProps extends InputComponentProps<string | undefined> {
  dataLoading: boolean;
  dataError: GetDataError<ResponseApiErrors> | null;
  options?: Token[];
}

export const TokenInput: React.FC<TokenInputProps> = ({
  dataLoading,
  dataError,
  options,
  value,
  name,
  onBlur,
  onChange,
  disabled,
  error,
  helperText,
}: TokenInputProps) => {
  if (dataLoading) {
    return <CircularProgress size="32px" />;
  }

  if (dataError || !options) {
    return <Typography color="error">Unable to load tokens</Typography>;
  }

  const mappedOptions = options.map((token) => ({ label: token.name ?? '', value: token.uid }));

  return (
    <AutocompleteWrapper>
      <Autocomplete
        disableClearable
        options={mappedOptions}
        getOptionLabel={({ label }) => label}
        value={mappedOptions.find(({ value: key }) => key === value)}
        renderOption={(
          {
            'aria-disabled': ariaDisabled,
            'aria-selected': ariaSelected,
            className,
            onClick,
            onMouseMove,
            onTouchStart,
            role,
            tabIndex,
          },
          option,
          state,
        ) => (
          <AutocompleteOption
            aria-disabled={ariaDisabled}
            aria-selected={ariaSelected}
            className={className}
            index={state.index}
            key={uid(option)}
            onClick={onClick}
            onMouseMove={onMouseMove}
            onTouchStart={onTouchStart}
            role={role}
            tabIndex={tabIndex}
          >
            <div>
              <Typography variant="body2">{option.label}</Typography>
              <Typography variant="body2" color="textSecondary">
                {option.value}
              </Typography>
            </div>
          </AutocompleteOption>
        )}
        renderInput={renderAutocompleteInput(disabled, error, helperText)}
        disabled={disabled}
        onBlur={onBlur}
        onChange={(_, token: AutocompleteContents<string | undefined>) => {
          // Note: this also blocks the user from manually clearing the field
          if (token && token.value) {
            onChange(name)(token.value);
          }
        }}
        style={{ width: '100%' }}
      />
    </AutocompleteWrapper>
  );
};
